import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getSupabaseClient } from '@/lib/supabase';

export default function SignUpPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { signUp, signInWithOAuth, user } = useAuth();

  const supabaseReady = useMemo(() => !!getSupabaseClient(), []);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate('/', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!supabaseReady) {
      setError(t('signUp.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      return;
    }

    if (password !== confirmPassword) {
      setError(t('signUp.error.passwordMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('signUp.error.weakPassword'));
      return;
    }

    setLoading(true);

    try {
      const result = await signUp(email, password, { full_name: fullName });
      if (result?.session) {
        // 이메일 확인 없이 바로 로그인됨
        navigate('/', { replace: true });
      } else {
        // 이메일 확인 필요
        setSuccess(true);
      }
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Supabase not configured') || msg.includes('not connected')) {
        setError(t('signUp.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      } else {
        setError(msg || t('signUp.error.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOAuth = async (provider: 'google' | 'facebook') => {
    setError('');
    if (!supabaseReady) {
      setError(t('signUp.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      return;
    }
    try {
      await signInWithOAuth(provider);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Supabase not configured') || msg.includes('not connected')) {
        setError(t('signUp.error.noSupabase') || 'Server connection is not configured. Please contact support.');
      } else {
        setError(msg || t('signUp.error.generic'));
      }
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl border border-stone-200 p-8 shadow-sm text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-check-line text-2xl text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-stone-900 mb-2">{t('signUp.success')}</h2>
            <p className="text-sm text-stone-500 mb-6">
              We have sent a verification email to <strong>{email}</strong>.
              Please check your inbox and click the link to activate your account.
            </p>
            <a
              href="/sign-in"
              className="inline-block px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap"
            >
              Go to Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-stone-200 p-8 shadow-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-amber-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-box-3-line text-white text-2xl" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900">{t('signUp.title')}</h1>
            <p className="text-sm text-stone-500 mt-1">{t('signUp.subtitle')}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t('signUp.fullName')}
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t('signUp.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t('signUp.password')}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                placeholder="••••••••"
              />
              <p className="text-xs text-stone-400 mt-1">{t('signUp.passwordHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t('signUp.confirmPassword')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-3.5 py-2.5 text-sm border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                required
                className="mt-0.5 w-4 h-4 text-amber-600 border-stone-300 rounded focus:ring-amber-500"
              />
              <label htmlFor="terms" className="text-xs text-stone-500 leading-relaxed">
                {t('signUp.terms')}
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !agreed}
              className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  Loading...
                </span>
              ) : (
                t('signUp.button')
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-stone-400">{t('signUp.or')}</span>
            </div>
          </div>

          {/* OAuth */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleOAuth('google')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-stone-300 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-google-fill text-lg text-red-500" />
              {t('signUp.google')}
            </button>
            <button
              onClick={() => handleOAuth('facebook')}
              className="flex items-center justify-center gap-2 px-4 py-2.5 border border-stone-300 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-facebook-fill text-lg text-blue-600" />
              {t('signUp.facebook')}
            </button>
          </div>

          {/* Sign in link */}
          <p className="text-center text-sm text-stone-500 mt-6">
            {t('signUp.hasAccount')}{' '}
            <a href="/sign-in" className="text-amber-600 hover:text-amber-700 font-semibold">
              {t('signUp.signInLink')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}